import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Phone, ChevronRight } from "lucide-react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { CallButton, GetStartedButton } from "@/components/CTA";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SITE, IMAGES } from "@/data/site";
import { SERVICES, getService, CATEGORY_LABELS } from "@/data/services";
import NotFoundPage from "@/pages/NotFoundPage";

const EASE = [0.22, 1, 0.36, 1];

export default function ServicePage() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <NotFoundPage />;

  const related = service.related.map(getService).filter(Boolean);

  return (
    <>
      <Seo title={service.seoTitle} siteName={SITE.siteName} description={service.seoDescription} />
      <section className="grain relative overflow-hidden bg-navy" data-testid="service-hero">
        <div className="absolute inset-y-0 right-0 hidden w-1/2 lg:block">
          <img src={IMAGES.passport} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-20" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/70 to-navy/30" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-1.5 text-xs text-slate-400"
            data-testid="service-breadcrumb"
          >
            <Link to="/" className="transition-colors hover:text-gold">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/services" className="transition-colors hover:text-gold">Services</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-gold">{service.name}</span>
          </motion.nav>
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 inline-block rounded-full border border-gold/40 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-gold"
            data-testid="service-private-badge"
          >
            Private Service Provider
          </motion.span>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl" data-testid="service-h1">
            <span className="mask-line">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: EASE }}>
                {service.h1}
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
            className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            {service.tagline}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <GetStartedButton testId="service-hero-get-started" />
            <CallButton light testId="service-hero-call-now" />
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold-dark">{CATEGORY_LABELS[service.category]}</p>
              <p className="mt-4 text-lg leading-relaxed text-slate-700">{service.intro}</p>
              <div className="mt-5 space-y-4">
                {service.paragraphs.map((p, i) => (
                  <p key={i} className="text-base leading-relaxed text-slate-600">{p}</p>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-8" data-testid="service-assist-card">
                <h2 className="font-display text-xl font-bold text-navy">What We Assist With</h2>
                <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                  {service.assistWith.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 rounded-xl border-l-4 border-gold bg-slate-50 p-6" data-testid="service-fee-note">
                <p className="text-sm font-semibold text-navy">Fees & Processing</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Government agencies and consulates charge their own fees, paid to the government. Our professional service fees are separate and explained before work begins. {SITE.processingDisclaimer}
                </p>
              </div>
            </Reveal>
          </div>
          <div>
            <Reveal delay={0.1}>
              <div id="get-started" className="scroll-mt-28 rounded-2xl bg-navy p-7 shadow-xl" data-testid="service-form-card">
                <div className="gold-rule mb-5 w-14" />
                <h2 className="font-display text-xl font-bold text-white">Tell Us What You Need Help With</h2>
                <p className="mb-6 mt-2 text-sm text-slate-400">Our team will follow up with next steps.</p>
                <LeadForm dark source={`service-${service.slug}`} defaultService={service.name} testId="service-lead-form" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16 lg:py-24" data-testid="service-faq-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
              {service.name} — Common Questions
            </h2>
            <Accordion type="single" collapsible className="mt-8" data-testid="service-faq-accordion">
              {service.faqs.map((f, i) => (
                <AccordionItem key={i} value={`sf-${i}`} data-testid={`service-faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base font-semibold text-navy hover:text-gold-dark">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16" data-testid="service-related-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-navy">Related Services</h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  data-testid={`related-service-${r.slug}`}
                  className="group rounded-xl border border-slate-200 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold/60 hover:shadow-lg"
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold-dark">{CATEGORY_LABELS[r.category]}</p>
                  <p className="mt-2 font-display text-lg font-bold text-navy group-hover:text-gold-dark">{r.name}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500">
                    Learn More <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="grain bg-navy-deep py-16" data-testid="service-bottom-cta">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 px-4 text-center sm:px-6">
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">Ready to Get Started?</h2>
          <p className="max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Tell us about your travel plans and the documentation assistance you need. Our team can help you understand the next steps.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <GetStartedButton testId="service-bottom-get-started" />
            <a href={SITE.phoneTel} data-testid="service-bottom-call" className="inline-flex items-center gap-2.5 rounded-full border border-white/30 px-7 py-3.5 font-display text-sm font-semibold text-white transition-colors hover:border-gold hover:text-gold">
              <Phone className="h-4 w-4" /> CALL {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
